import React, { useState } from 'react';
import { useList } from "@refinedev/core";
import { Card, Col, DatePicker, Row, Select, Statistic, Table, Typography, Button, message } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { ArrowDownOutlined, DownloadOutlined } from '@ant-design/icons';
import { downloadCsvReport, downloadPdfReport } from '../../utils/reportExport';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Line, LineChart, Legend } from 'recharts';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title } = Typography;

export const PurchaseReport: React.FC = () => {
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().subtract(30, 'days').startOf('day'),
        dayjs().endOf('day')
    ]);
    const [selectedVendor, setSelectedVendor] = useState<string | undefined>();
    const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>();

    // Fetch purchase data with server-side filtering
    const { data: purchasesData, isLoading: purchasesLoading } = useList({
        resource: "purchases",
        dataProviderName: "default",
        filters: [
            ...(selectedVendor ? [{
                field: "vendorId",
                operator: "eq",
                value: selectedVendor,
            }] : []),
            ...(selectedCurrency ? [{
                field: "currencyTypeCode",
                operator: "eq",
                value: selectedCurrency,
            }] : []),
            {
                field: "fromDate",
                operator: "eq",
                value: dateRange[0]?.toISOString(),
            },
            {
                field: "toDate",
                operator: "eq",
                value: dateRange[1]?.toISOString(),
            },
        ],
        meta: {
            fields: [
                "id",
                "reference",
                "purchaseDate",
                "vendorId",
                "vendorName",
                "totalAmount",
                "status",
                "currencyTypeCode",
                "exchangeRate",
                {
                    items: [
                        "itemName",
                        "quantity",
                        "unitPrice",
                        "totalAmount"
                    ]
                }
            ],
        },
    });

    // Fetch currencies for filter
    const { data: currenciesData } = useList({
        resource: "currency",
        dataProviderName: "default",
        meta: {
            fields: ["id", "currencyCode", "name", "symbol"],
        },
    });

    // Fetch vendors for filter
    const { data: vendorsData } = useList({
        resource: "vendors",
        dataProviderName: "default",
        meta: {
            fields: ["id", "name", "code"],
        },
    });

    // Define types for purchase data
    interface PurchaseData {
        id: string;
        reference: string;
        purchaseDate: string;
        vendorId: string;
        vendorName: string;
        totalAmount: number;
        status: string;
        currencyTypeCode: string;
        exchangeRate: number;
        items?: PurchaseItem[];
        [key: string]: any;
    }

    interface PurchaseItem {
        itemName: string;
        quantity: number;
        unitPrice: number;
        totalAmount: number;
        [key: string]: any;
    }

    interface CurrencyItem {
        id: string;
        currencyCode: string;
        name: string;
        symbol: string;
        [key: string]: any;
    }

    // Group purchases by currency
    const purchasesByCurrency = (purchasesData?.data || []).reduce((acc: Record<string, PurchaseData[]>, purchase: PurchaseData) => {
        const currencyCode = purchase.currencyTypeCode || 'Unknown';
        if (!acc[currencyCode]) {
            acc[currencyCode] = [];
        }
        acc[currencyCode].push(purchase);
        return acc;
    }, {} as Record<string, PurchaseData[]>);

    // Calculate statistics for each currency
    const currencyStats = Object.entries(purchasesByCurrency).map(([currencyCode, purchases]) => {
        const typedPurchases = purchases as PurchaseData[];
        const totalAmount = typedPurchases.reduce((sum: number, purchase: PurchaseData) => sum + purchase.totalAmount, 0);
        const purchaseCount = typedPurchases.length;
        return {
            currencyCode,
            totalAmount,
            purchaseCount,
            averagePurchase: purchaseCount > 0 ? totalAmount / purchaseCount : 0
        };
    });

    // Calculate total purchases
    const totalPurchases = (purchasesData?.data || []).reduce(
        (sum, purchase) => sum + purchase.totalAmount,
        0
    );

    // Process data by date for trend chart with currency separation
    const purchasesByDateAndCurrency = (purchasesData?.data || []).reduce((acc: Record<string, Record<string, number>>, purchase: PurchaseData) => {
        const date = dayjs(purchase.purchaseDate).format('YYYY-MM-DD');
        const currencyCode = purchase.currencyTypeCode || 'Unknown';

        if (!acc[date]) {
            acc[date] = {};
        }

        if (!acc[date][currencyCode]) {
            acc[date][currencyCode] = 0;
        }

        acc[date][currencyCode] += purchase.totalAmount;
        return acc;
    }, {} as Record<string, Record<string, number>>);

    const trendChartData = Object.keys(purchasesByDateAndCurrency)
        .sort()
        .map(date => {
            const entry: any = { date };

            Object.entries(purchasesByDateAndCurrency[date]).forEach(([currencyCode, amount]) => {
                entry[currencyCode] = amount;
                entry[`${currencyCode}_currency`] = currencyCode; // Store currency for tooltip
            });

            return entry;
        });

    // Process data by vendor with currency separation
    const purchasesByVendorAndCurrency = (purchasesData?.data || []).reduce((acc: Record<string, Record<string, any>>, purchase: PurchaseData) => {
        const vendorId = purchase.vendorId;
        const currencyCode = purchase.currencyTypeCode || 'Unknown';

        if (!acc[vendorId]) {
            acc[vendorId] = {
                vendorId,
                vendorName: purchase.vendorName,
                currencies: {}
            };
        }

        if (!acc[vendorId].currencies[currencyCode]) {
            acc[vendorId].currencies[currencyCode] = {
                totalAmount: 0,
                purchaseCount: 0,
            };
        }

        acc[vendorId].currencies[currencyCode].totalAmount += purchase.totalAmount;
        acc[vendorId].currencies[currencyCode].purchaseCount += 1;
        return acc;
    }, {} as Record<string, Record<string, any>>);

    // Flatten for display
    const vendorChartData = Object.values(purchasesByVendorAndCurrency)
        .map((vendor: any) => {
            const vendorEntry: Record<string, any> = {
                vendorId: vendor.vendorId,
                vendorName: vendor.vendorName,
            };

            // Type assertion for vendor.currencies to avoid Object.entries error
            const currencies = vendor.currencies as Record<string, { totalAmount: number; purchaseCount: number }>;
            Object.entries(currencies).forEach(([currencyCode, stats]) => {
                vendorEntry[`totalAmount_${currencyCode}`] = stats.totalAmount;
                vendorEntry[`purchaseCount_${currencyCode}`] = stats.purchaseCount;
            });

            return vendorEntry;
        })
        .sort((a: any, b: any) => {
            // Sort by selected currency if present, otherwise by first currency
            if (selectedCurrency) {
                return (b[`totalAmount_${selectedCurrency}`] || 0) - (a[`totalAmount_${selectedCurrency}`] || 0);
            }

            // Find first currency in both entries
            const aFirstCurrency = Object.keys(a).find(key => key.startsWith('totalAmount_'));
            const bFirstCurrency = Object.keys(b).find(key => key.startsWith('totalAmount_'));

            if (aFirstCurrency && bFirstCurrency) {
                return b[aFirstCurrency] - a[aFirstCurrency];
            }

            return 0;
        })
        .slice(0, 10);

    // Process data by item with currency
    const purchasesByItemAndCurrency = (purchasesData?.data || []).reduce((acc: Record<string, Record<string, any>>, purchase: PurchaseData) => {
        const currencyCode = purchase.currencyTypeCode || 'Unknown';

        purchase.items?.forEach((item: PurchaseItem) => {
            if (!acc[item.itemName]) {
                acc[item.itemName] = {};
            }

            if (!acc[item.itemName][currencyCode]) {
                acc[item.itemName][currencyCode] = {
                    quantity: 0,
                    totalAmount: 0,
                };
            }

            acc[item.itemName][currencyCode].quantity += item.quantity;
            acc[item.itemName][currencyCode].totalAmount += item.totalAmount;
        });

        return acc;
    }, {} as Record<string, Record<string, any>>);

    // Flatten for display
    const topItemsData = Object.entries(purchasesByItemAndCurrency)
        .map(([itemName, currencies]) => {
            const itemEntry: Record<string, any> = {
                itemName,
            };

            // Type assertion for currencies to avoid Object.entries error
            const typedCurrencies = currencies as Record<string, { quantity: number; totalAmount: number }>;
            Object.entries(typedCurrencies).forEach(([currencyCode, stats]) => {
                itemEntry[`quantity_${currencyCode}`] = stats.quantity;
                itemEntry[`totalAmount_${currencyCode}`] = stats.totalAmount;
            });

            return itemEntry;
        })
        .sort((a: any, b: any) => {
            // Sort by selected currency if present, otherwise by first currency
            if (selectedCurrency) {
                return (b[`totalAmount_${selectedCurrency}`] || 0) - (a[`totalAmount_${selectedCurrency}`] || 0);
            }

            // Find first currency in both entries
            const aFirstCurrency = Object.keys(a).find(key => key.startsWith('totalAmount_'));
            const bFirstCurrency = Object.keys(b).find(key => key.startsWith('totalAmount_'));

            if (aFirstCurrency && bFirstCurrency) {
                return b[aFirstCurrency] - a[aFirstCurrency];
            }

            return 0;
        })
        .slice(0, 10);

    // Purchase columns
    const columns: ColumnType<PurchaseData>[] = [
        {
            title: 'Reference',
            dataIndex: 'reference',
            key: 'reference',
        },
        {
            title: 'Date',
            dataIndex: 'purchaseDate',
            key: 'purchaseDate',
            render: (value: string) => dayjs(value).format('YYYY-MM-DD'),
            sorter: (a: PurchaseData, b: PurchaseData) => dayjs(a.purchaseDate).unix() - dayjs(b.purchaseDate).unix(),
        },
        {
            title: 'Vendor',
            dataIndex: 'vendorName',
            key: 'vendorName',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
        },
        {
            title: 'Currency',
            dataIndex: 'currencyTypeCode',
            key: 'currencyTypeCode',
        },
        {
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (value: number, record: PurchaseData) => {
                const symbol = currenciesData?.data?.find((c: CurrencyItem) => c.currencyCode === record.currencyTypeCode)?.symbol || '$';
                return `${symbol}${value.toFixed(2)}`;
            },
            sorter: (a: PurchaseData, b: PurchaseData) => a.totalAmount - b.totalAmount,
        },
    ];

    // Handle currency selection
    const handleCurrencyChange = (value: string | undefined) => {
        setSelectedCurrency(value);
    };

    const purchases = (purchasesData?.data || []) as PurchaseData[];
    const exportSubtitle = [
        `${dateRange[0].format('YYYY-MM-DD')} – ${dateRange[1].format('YYYY-MM-DD')}`,
        selectedVendor ? `Vendor filter: ${selectedVendor}` : '',
        selectedCurrency ? `Currency: ${selectedCurrency}` : '',
    ]
        .filter(Boolean)
        .join(' · ');

    const handleExportExcel = () => {
        const headers = ['Reference', 'Date', 'Vendor', 'Status', 'Currency', 'Total Amount'];
        const rows = purchases.map((p) => [
            p.reference,
            dayjs(p.purchaseDate).format('YYYY-MM-DD'),
            p.vendorName,
            p.status,
            p.currencyTypeCode ?? '',
            Number(p.totalAmount).toFixed(2),
        ]);
        downloadCsvReport('purchase-report', headers, rows);
        message.success('Export downloaded (CSV opens in Excel).');
    };

    const handleExportPdf = async () => {
        const headers = ['Ref.', 'Date', 'Vendor', 'Status', 'Cur.', 'Amount'];
        const body = purchases.map((p) => [
            p.reference,
            dayjs(p.purchaseDate).format('YYYY-MM-DD'),
            p.vendorName,
            p.status,
            p.currencyTypeCode ?? '',
            Number(p.totalAmount).toFixed(2),
        ]);
        try {
            await downloadPdfReport('Purchase report', exportSubtitle, headers, body);
            message.success('PDF downloaded.');
        } catch {
            message.error('Could not generate PDF.');
        }
    };

    return (
        <div className="space-y-4">
            <Title level={2}>Purchase Report</Title>

            <Card>
                <div className="flex flex-wrap gap-4 mb-4">
                    <RangePicker
                        value={dateRange}
                        onChange={(dates) => dates && setDateRange(dates)}
                        allowClear={false}
                    />
                    <Select
                        style={{ width: 250 }}
                        placeholder="Select Vendor"
                        allowClear
                        onChange={setSelectedVendor}
                        options={vendorsData?.data?.map((vendor: { id: string; name: string; code: string }) => ({
                            value: vendor.id,
                            label: `${vendor.name} (${vendor.code})`,
                        }))}
                        showSearch
                        optionFilterProp="label"
                    />
                    <Select
                        style={{ width: 250 }}
                        placeholder="Select Currency"
                        allowClear
                        onChange={handleCurrencyChange}
                        options={currenciesData?.data?.map((currency: CurrencyItem) => ({
                            value: currency.currencyCode,
                            label: `${currency.name} (${currency.currencyCode})`,
                        }))}
                        showSearch
                        optionFilterProp="label"
                    />
                    <div style={{ marginLeft: 'auto' }}>
                        <Button icon={<DownloadOutlined />} style={{ marginRight: '8px' }} onClick={handleExportExcel}>
                            Export to Excel
                        </Button>
                        <Button icon={<DownloadOutlined />} onClick={() => void handleExportPdf()}>
                            Export to PDF
                        </Button>
                    </div>
                </div>

                {selectedCurrency ? (
                    // Show stats only for the selected currency
                    <Row gutter={16}>
                        <Col span="8">
                            <Card>
                                <Statistic
                                    title={`Total Purchases (${selectedCurrency})`}
                                    value={
                                        currencyStats.find(stat => stat.currencyCode === selectedCurrency)?.totalAmount || 0
                                    }
                                    precision={2}
                                    prefix={currenciesData?.data?.find((c: CurrencyItem) => c.currencyCode === selectedCurrency)?.symbol || '$'}
                                    valueStyle={{ color: '#cf1322' }}
                                    suffix={<ArrowDownOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col span="8">
                            <Card>
                                <Statistic
                                    title={`Purchase Orders (${selectedCurrency})`}
                                    value={
                                        currencyStats.find(stat => stat.currencyCode === selectedCurrency)?.purchaseCount || 0
                                    }
                                />
                            </Card>
                        </Col>
                        <Col span="8">
                            <Card>
                                <Statistic
                                    title={`Average Purchase (${selectedCurrency})`}
                                    value={
                                        currencyStats.find(stat => stat.currencyCode === selectedCurrency)?.averagePurchase || 0
                                    }
                                    precision={2}
                                    prefix={currenciesData?.data?.find((c: CurrencyItem) => c.currencyCode === selectedCurrency)?.symbol || '$'}
                                />
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    // Show stats for all currencies
                    <div>
                        {currencyStats.map((stat) => (
                            <Row gutter={16} key={stat.currencyCode} style={{ marginBottom: 16 }}>
                                <Col span="24">
                                    <Title level={4}>{stat.currencyCode} Statistics</Title>
                                </Col>
                                <Col span="8">
                                    <Card>
                                        <Statistic
                                            title={`Total Purchases (${stat.currencyCode})`}
                                            value={stat.totalAmount}
                                            precision={2}
                                            prefix={currenciesData?.data?.find((c: CurrencyItem) => c.currencyCode === stat.currencyCode)?.symbol || '$'}
                                            valueStyle={{ color: '#cf1322' }}
                                            suffix={<ArrowDownOutlined />}
                                        />
                                    </Card>
                                </Col>
                                <Col span="8">
                                    <Card>
                                        <Statistic
                                            title={`Purchase Orders (${stat.currencyCode})`}
                                            value={stat.purchaseCount}
                                        />
                                    </Card>
                                </Col>
                                <Col span="8">
                                    <Card>
                                        <Statistic
                                            title={`Average Purchase (${stat.currencyCode})`}
                                            value={stat.averagePurchase}
                                            precision={2}
                                            prefix={currenciesData?.data?.find((c: CurrencyItem) => c.currencyCode === stat.currencyCode)?.symbol || '$'}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        ))}
                    </div>
                )}
            </Card>

            <Card title="Purchase Trend">
                <div style={{ height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip
                                formatter={(value, name, props) => {
                                    const currencyCode = name;
                                    const symbol = currenciesData?.data?.find((c: any) => c.currencyCode === currencyCode)?.symbol || '$';
                                    return [`${symbol}${value.toFixed(2)}`, `${currencyCode}`];
                                }}
                            />
                            <Legend />
                            {Object.keys(purchasesByCurrency).map((currencyCode, index) => (
                                <Line
                                    key={currencyCode}
                                    type="monotone"
                                    dataKey={currencyCode}
                                    name={currencyCode}
                                    stroke={`hsl(${index * 45}, 70%, 50%)`}
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                    activeDot={{ r: 8 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            <Row gutter={16}>
                <Col span={12}>
                    <Card title="Top Vendors">
                        <div style={{ height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={vendorChartData}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis dataKey="vendorName" type="category" width={150} />
                                    <Tooltip formatter={(value) => [`$${(value as number).toFixed(2)}`, 'Amount']} />
                                    {selectedCurrency ? (
                                        <Bar dataKey={`totalAmount_${selectedCurrency}`} fill="#82ca9d" name={`${selectedCurrency} Amount`} />
                                    ) : (
                                        Object.keys(purchasesByCurrency).map((currency, index) => (
                                            <Bar key={currency} dataKey={`totalAmount_${currency}`} fill={`hsl(${index * 45}, 70%, 50%)`} name={`${currency} Amount`} />
                                        ))
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Top Items Purchased">
                        <div style={{ height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={topItemsData}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis dataKey="itemName" type="category" width={150} />
                                    <Tooltip formatter={(value) => [`$${(value as number).toFixed(2)}`, 'Amount']} />
                                    {selectedCurrency ? (
                                        <Bar dataKey={`totalAmount_${selectedCurrency}`} fill="#8884d8" name={`${selectedCurrency} Amount`} />
                                    ) : (
                                        Object.keys(purchasesByCurrency).map((currency, index) => (
                                            <Bar key={currency} dataKey={`totalAmount_${currency}`} fill={`hsl(${index * 45 + 180}, 70%, 50%)`} name={`${currency} Amount`} />
                                        ))
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>
            </Row>

            <Card title="Purchases List">
                <Table
                    columns={columns as any}
                    dataSource={purchasesData?.data || []}
                    loading={purchasesLoading}
                    rowKey={(record) => record.id}
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        </div>
    );
};